import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { getVendors, getProductsForPO, createPurchaseOrder, getMetricsForProducts } from '../services/ClientService';
import { toast } from 'react-toastify';
import { customSelect } from './Styles/CustomStyles';
import { FaInfo, FaInfoCircle, FaPlus, FaWarehouse } from 'react-icons/fa';

const CreatePurchaseOrder = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVendor, setSelectedVendor] = useState('');
    const [notes, setNotes] = useState('');
    const [selectedProducts, setSelectedProducts] = useState({});
    const [selectAll, setSelectAll] = useState(false);
    const [bulkUnits, setBulkUnits] = useState('');
    
    // Common Inventory States
    const [metrics, setMetrics] = useState([]);
    const [poCommonInventoryData, setPoCommonInventoryData] = useState({});

    useEffect(() => {
        fetchVendors();
        fetchProducts();
        fetchMetrics();
    }, []);

    const fetchMetrics = async () => {
        try {
            const response = await getMetricsForProducts({});
            if (response.code === 200 && response.success) {
                const formattedMetrics = [];
                response.data.forEach(metric => {
                    // Add primary metric
                    formattedMetrics.push({
                        value: `${metric._id}_primary`,
                        label: metric.metricLabel,
                        metricId: metric._id,
                        metricType: 'primary',
                        metricLabel: metric.metricLabel,
                        metricName: metric.metricName
                    });
                    
                    // Add supporting metric if exists
                    if (metric.supportingMetricLabel) {
                        formattedMetrics.push({
                            value: `${metric._id}_supporting`,
                            label: metric.supportingMetricLabel,
                            metricId: metric._id,
                            metricType: 'supporting',
                            metricLabel: metric.supportingMetricLabel,
                            metricName: metric.supportingMetricName
                        });
                    }
                });
                setMetrics(formattedMetrics);
            }
        } catch (error) {
            console.error('Error fetching metrics:', error);
        }
    };

    const fetchVendors = async () => {
        try {
            const response = await getVendors({});
            setVendors(response.vendors || []);
        } catch (error) {
            console.error('Error fetching vendors:', error);
            toast.error('Failed to fetch vendors');
        }
    };

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await getProductsForPO();
            let productsData = response.products || [];
            
            // Transform API response to create individual rows for regular products
            // while keeping options grouped for common inventory products
            const normalizedProducts = [];
            
            productsData.forEach(product => {
                if (product.commonInventoryType) {
                    // For common inventory products, keep the full product with options array
                    normalizedProducts.push(product);
                } else if (product.options && Array.isArray(product.options)) {
                    // For regular products, create individual entries for each option
                    product.options.forEach(option => {
                        normalizedProducts.push({
                            _id: product._id,
                            productName: product.productName,
                            productImage: product.productImage,
                            productOptionId: option._id,
                            weight: option.weight,
                            originalPrice: option.originalPrice,
                            barcode: option.barcode,
                            commonInventoryType: false
                        });
                    });
                } else {
                    // Fallback if no options array
                    normalizedProducts.push(product);
                }
            });
            
            setProducts(normalizedProducts);
            setFilteredProducts(normalizedProducts);
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm.trim() === '') {
                setFilteredProducts(products);
            } else {
                const filtered = products.filter(product =>
                    product.productName.toLowerCase().includes(searchTerm.toLowerCase())
                );
                setFilteredProducts(filtered);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm, products]);

    const handleProductSelect = (productId, checked) => {
        if (checked) {
            setSelectedProducts(prev => ({
                ...prev,
                [productId]: {
                    selected: true,
                    requestPrice: '',
                    margin: '',
                    unitsRequired: ''
                }
            }));
        } else {
            setSelectedProducts(prev => {
                const updated = { ...prev };
                delete updated[productId];
                return updated;
            });
        }
    };

    const handleRequestPriceChange = (productId, value, originalPrice) => {
        const requestPrice = parseFloat(value) || 0;
        const margin = originalPrice > 0 ? ((originalPrice - requestPrice) / originalPrice * 100).toFixed(2) : 0;

        setSelectedProducts(prev => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                requestPrice: value,
                margin: margin
            }
        }));
    };

    const handleMarginChange = (productId, value, originalPrice) => {
        const margin = parseFloat(value) || 0;
        const requestPrice = (originalPrice - (originalPrice * margin / 100)).toFixed(2);

        setSelectedProducts(prev => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                margin: value,
                requestPrice: requestPrice
            }
        }));
    };

    const handleUnitsChange = (productId, value) => {
        setSelectedProducts(prev => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                unitsRequired: value
            }
        }));
    };

    // Handle Select All
    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        
        if (checked) {
            const newSelected = {};
            filteredProducts.forEach(product => {
                const productId = product.commonInventoryType 
                    ? product._id.toString() 
                    : product.productOptionId?.toString();
                
                if (!productId) return; // Skip if no valid ID
                
                newSelected[productId] = selectedProducts[productId] || {
                    selected: true,
                    requestPrice: '',
                    margin: '',
                    unitsRequired: ''
                };
                newSelected[productId].selected = true;
            });
            setSelectedProducts(newSelected);
        } else {
            const newSelected = { ...selectedProducts };
            filteredProducts.forEach(product => {
                const productId = product.commonInventoryType 
                    ? product._id.toString() 
                    : product.productOptionId?.toString();
                
                if (!productId) return; // Skip if no valid ID
                
                if (newSelected[productId]) {
                    delete newSelected[productId];
                }
            });
            setSelectedProducts(newSelected);
        }
    };

    // Handle Bulk Units Update
    const handleBulkUnitsUpdate = () => {
        if (!bulkUnits || bulkUnits <= 0) {
            toast.error('Please enter a valid number of units');
            return;
        }

        const updatedProducts = { ...selectedProducts };
        Object.keys(updatedProducts).forEach(productId => {
            if (updatedProducts[productId].selected) {
                updatedProducts[productId].unitsRequired = bulkUnits;
            }
        });
        setSelectedProducts(updatedProducts);
        toast.info(`Updated ${Object.keys(updatedProducts).filter(id => updatedProducts[id].selected).length} products with ${bulkUnits} units`);
    };

    // Common Inventory Handlers (mirroring bulk distribution pattern)
    const handlePoCommonInventoryCheckboxChange = (productId, checked, product) => {
        setPoCommonInventoryData(prev => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                isCommonInventory: checked,
                quantity: '',
                mainMetric: product?.mainMetric || null,
                originalPrice: ''
            }
        }));
    };



    const handlePoCommonInventoryQuantityChange = (productId, value) => {
        setPoCommonInventoryData(prev => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                quantity: value
            }
        }));
    };

    const handlePoCommonInventoryOriginalPriceChange = (productId, value) => {
        setPoCommonInventoryData(prev => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                originalPrice: value
            }
        }));
    };

    // Update selectAll checkbox based on filtered products selection
    useEffect(() => {
        if (filteredProducts.length === 0) {
            setSelectAll(false);
            return;
        }

        const allSelected = filteredProducts.every(product => {
            const productId = product.commonInventoryType 
                ? product._id.toString() 
                : product.productOptionId?.toString();
            
            if (!productId) return false; // Skip if no valid ID
            return selectedProducts[productId]?.selected;
        });
        setSelectAll(allSelected);
    }, [selectedProducts, filteredProducts]);

    const calculateSummary = () => {
        let totalProducts = 0;
        let totalMRP = 0;
        let totalRequestPrice = 0;
        let totalUnits = 0;

        Object.entries(selectedProducts).forEach(([productId, data]) => {
            if (data.selected) {
                // Handle both common inventory (using _id) and regular products (using productOptionId)
                const product = products.find(p => 
                    p.productOptionId?.toString() === productId || p._id.toString() === productId
                );
                if (product && !product.commonInventoryType) {
                    const units = parseInt(data.unitsRequired) || 0;
                    const requestPrice = parseFloat(data.requestPrice) || 0;
                    const margin = parseFloat(data.margin) || 0;
                    
                    // For selected products, all three fields must be filled: Request Price, Margin, and Units Required
                    if (units > 0 && requestPrice > 0 && margin > 0) {
                        totalProducts++;
                        totalMRP += product.originalPrice * units;
                        totalRequestPrice += requestPrice * units;
                        totalUnits += units;
                    }
                }
            }
        });

        return {
            totalProducts,
            totalMRP: totalMRP.toFixed(2),
            totalRequestPrice: totalRequestPrice.toFixed(2),
            totalUnits,
            totalSavings: (totalMRP - totalRequestPrice).toFixed(2)
        };
    };

    const calculateCommonInventorySummary = () => {
        let totalMRP = 0;
        let totalRequestPrice = 0;

        Object.entries(poCommonInventoryData).forEach(([productId, data]) => {
            if (data.quantity && data.requestPrice && data.originalPrice) {
                const quantity = parseInt(data.quantity) || 0;
                const requestPrice = parseFloat(data.requestPrice) || 0;
                const originalPrice = parseFloat(data.originalPrice) || 0;
                
                if (quantity > 0 && requestPrice > 0 && originalPrice > 0) {
                    totalMRP += originalPrice * quantity;
                    totalRequestPrice += requestPrice * quantity;
                }
            }
        });

        return {
            totalMRP: totalMRP.toFixed(2),
            totalRequestPrice: totalRequestPrice.toFixed(2),
            totalSavings: (totalMRP - totalRequestPrice).toFixed(2),
            hasCommonInventory: Object.keys(poCommonInventoryData).length > 0
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedVendor) {
            toast.error('Please select a vendor');
            return;
        }

        const productsForPO = [];
        
        // Process regular selected products
        Object.entries(selectedProducts).forEach(([productId, data]) => {
            if (data.selected) {
                const product = products.find(p => p.productOptionId && p.productOptionId.toString() === productId);
                
                // Skip if this is a common inventory product (will be handled separately)
                if (product?.commonInventoryType) {
                    return;
                }
                
                if (product) {
                    const units = parseInt(data.unitsRequired) || 0;
                    const requestPrice = parseFloat(data.requestPrice) || 0;

                    if (units > 0 && requestPrice > 0) {
                        productsForPO.push({
                            productId: product._id,
                            productName: product.productName,
                            productOptionId: product.productOptionId,
                            weight: product.weight,
                            originalPrice: product.originalPrice,
                            requestPrice: requestPrice,
                            unitsRequired: units
                        });
                    }
                }
            }
        });

        // Process common inventory products
        products.forEach(product => {
            if (product?.commonInventoryType) {
                const commonData = poCommonInventoryData[product._id];
                
                if (commonData?.isCommonInventory) {
                    if (!commonData?.mainMetric && !product?.mainMetric) {
                        toast.error(`Main metric not found for ${product.productName}`);
                        return;
                    }
                    
                    const quantity = commonData.quantity === '' ? 0 : parseInt(commonData.quantity, 10);
                    if (isNaN(quantity) || quantity <= 0) {
                        toast.error(`Please enter a valid quantity for ${product.productName}`);
                        return;
                    }
                    
                    const requestPrice = parseFloat(commonData.requestPrice) || 0;
                    if (requestPrice <= 0) {
                        toast.error(`Please enter a valid request price for ${product.productName}`);
                        return;
                    }
                    
                    // Get the product's main option data for reference
                    const mainOption = product.options?.[0];
                    const mainMetricToUse = commonData?.mainMetric || product?.mainMetric;
                    
                    // Add an entry for EACH product option with the same quantity and metric
                    if (product.options && product.options.length > 0) {
                        product.options.forEach(option => {
                            productsForPO.push({
                                productId: product._id,
                                productName: product.productName,
                                productOptionId: option._id,
                                weight: option.weight,
                                originalPrice: commonData.originalPrice ? parseFloat(commonData.originalPrice) : product.originalPrice,
                                requestPrice: requestPrice,
                                requiredQuantity: quantity,
                                commonInventory: true,
                                selectedMetric: mainMetricToUse?.metricLabel,
                                metricId: mainMetricToUse?.metricId
                            });
                        });
                    }
                }
            }
        });

        if (productsForPO.length === 0) {
            toast.error('Please select at least one product with valid quantity and price');
            return;
        }

        setLoading(true);
        try {
            const response = await createPurchaseOrder({
                vendorId: selectedVendor,
                products: productsForPO,
                notes
            });
            toast.success(`Purchase Order created successfully! PO Number: ${response.poNumber}`);
            navigate('/purchase-orders');
        } catch (error) {
            console.error('Error creating purchase order:', error);
            toast.error(error.response?.data?.error || 'Failed to create purchase order');
        } finally {
            setLoading(false);
        }
    };

    const summary = calculateSummary();

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
            {/* Header */}
            <div className="bg-white shadow-md border-b border-gray-100 sticky top-0 z-10">
                <div className="px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/purchase-orders')}
                                className="text-gray-600 hover:text-gray-800 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <FiArrowLeft className="text-2xl" />
                            </button>
                            <h1 className="text-2xl font-bold text-gray-700">Create Purchase Order</h1>
                        </div>
                        <button
                            onClick={handleSubmit}
                            disabled={loading || (summary.totalProducts === 0 && !Object.values(poCommonInventoryData).some(data => data?.isCommonInventory && data?.quantity && data?.requestPrice))}
                            className="bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white px-6 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FiSave className="text-lg" />
                            {loading ? 'Creating...' : 'Create Purchase Order'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="px-4 sm:px-6 lg:px-8 py-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Vendor Selection */}
                    <div className="bg-white rounded-lg shadow-lg border border-gray-100 p-6">
                        <h2 className="text-xl font-bold text-gray-700 mb-4">Vendor Details</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block font-medium text-gray-700 mb-2">
                                    Select Vendor <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    value={selectedVendor ? vendors.find(v => v._id === selectedVendor) ? { value: selectedVendor, label: `${vendors.find(v => v._id === selectedVendor).vendorName} - ${vendors.find(v => v._id === selectedVendor).vendorMobile}` } : null : null}
                                    onChange={(option) => setSelectedVendor(option ? option.value : '')}
                                    options={vendors.map(vendor => ({
                                        value: vendor._id,
                                        label: `${vendor.vendorName} - ${vendor.vendorMobile}`
                                    }))}
                                    styles={customSelect}
                                    placeholder="Select Vendor"
                                    isClearable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                                <div>
                                <button
                                    type="button"
                                    onClick={() => navigate('/vendors')}
                                    className="mt-3 text-md text-blue-600 hover:underline"
                                >
                                    <FaInfoCircle className="inline mr-1 mb-0.5" /> Create new vendor if not listed above
                                </button>
                            </div>
                            </div>
                            <div>
                                <label className="block font-medium text-gray-700 mb-2">
                                    Notes (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-transparent focus:shadow-[0_0_0_4px_rgba(139,92,246,0.2)] hover:border-gray-300 transition-all duration-300 bg-white"
                                    placeholder="Add any special instructions or notes..."
                                />
                            </div>
                        </div>
                    </div>

                    {/* Products Table */}
                    <div className="bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                            <h2 className="text-xl font-bold text-gray-700 mb-4">Select Products</h2>
                            <p className="text-sm text-gray-600 mb-4">Check products to include in the purchase order</p>
                            
                            {/* Search and Bulk Actions */}
                            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                                {/* Search Bar */}
                                <div className="flex-1 max-w-md">
                                    <input
                                        type="text"
                                        placeholder="Search products by name..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-4 py-2.5 border-2 border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 focus:shadow-[0_0_0_4px_rgba(139,92,246,0.2)] hover:border-purple-300 transition-all duration-300 bg-white placeholder-gray-400"
                                    />
                                </div>

                                {/* Bulk Update Units */}
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min="1"
                                        placeholder="Bulk units"
                                        value={bulkUnits}
                                        onChange={(e) => setBulkUnits(e.target.value)}
                                        className="w-28 px-3 py-2.5 border-2 border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 focus:shadow-[0_0_0_4px_rgba(139,92,246,0.2)] hover:border-purple-300 transition-all duration-300"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleBulkUnitsUpdate}
                                        disabled={Object.keys(selectedProducts).filter(id => selectedProducts[id].selected).length === 0}
                                        className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                    >
                                        Bulk Update Units
                                    </button>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex justify-center items-center h-64">
                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="flex flex-col justify-center items-center h-64 text-gray-500">
                                <p className="text-lg font-medium">No products found</p>
                                <p className="text-sm mt-2">Try adjusting your search term</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectAll}
                                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                                        className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded cursor-pointer focus:outline-none"
                                                    />
                                                    <span>Select</span>
                                                </div>
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                S.No
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Product Name
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Weight
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Original Price (MRP)
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Request Price (₹)
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Margin (%)
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Qty / Metric
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredProducts.map((product, index) => {
                                            // For common inventory products, use _id; for regular, use productOptionId
                                            const productId = product.commonInventoryType 
                                                ? product._id.toString() 
                                                : product.productOptionId?.toString();
                                            
                                            if (!productId) return null; // Skip if no valid ID
                                            
                                            const isSelected = selectedProducts[productId]?.selected;
                                            const data = selectedProducts[productId] || {};

                                            // If this product has common inventory type, render special row
                                            if (product.commonInventoryType) {
                                                const commonData = poCommonInventoryData[product._id];
                                                
                                                return (
                                                    <tr key={productId} className="bg-indigo-50 border-l-4 border-indigo-500">
                                                        <td colSpan="8" className="px-4 py-4">
                                                            <div className="space-y-4">
                                                                {/* Checkbox and Product Name */}
                                                                <div className="flex items-center gap-3">
                                                                    <div className="flex items-center">
                                                                        <input
                                                                            type="checkbox"
                                                                            id={`common-${product._id}`}
                                                                            checked={commonData?.isCommonInventory || false}
                                                                            onChange={(e) => handlePoCommonInventoryCheckboxChange(product._id, e.target.checked, product)}
                                                                            className="h-5 w-5 text-indigo-600 rounded focus:outline-none focus:ring-0"
                                                                        />
                                                                    </div>
                                                                    <label htmlFor={`common-${product._id}`} className="font-semibold text-indigo-900 cursor-pointer">
                                                                        <FaWarehouse className="inline mr-2 text-sm" />
                                                                        {product.productName} <span className="text-gray-600 text-sm font-normal">(Common Inventory - Metric-based)</span>
                                                                    </label>
                                                                </div>

                                                                {/* Common Inventory Form */}
                                                                {commonData?.isCommonInventory && (
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-white rounded-lg p-4 border border-indigo-200">
                                                                        {/* Original Price */}
                                                                        <div>
                                                                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                                                Original Price (₹) / {poCommonInventoryData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel || 'unit'}
                                                                            </label>
                                                                            <input
                                                                                type="number"
                                                                                step="0.01"
                                                                                value={commonData.originalPrice || ''}
                                                                                onChange={(e) => handlePoCommonInventoryOriginalPriceChange(product._id, e.target.value)}
                                                                                placeholder="Enter or use default"
                                                                                className="w-full px-2 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                                                                            />
                                                                            <p className="text-[10px] text-gray-500 mt-1 mb-1">Price per 1 {poCommonInventoryData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel || 'unit'}</p>
                                                                        </div>

                                                                        {/* Request Price */}
                                                                        <div>
                                                                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                                                Request Price (₹) / {poCommonInventoryData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel || 'unit'} <span className="text-red-500">*</span>
                                                                            </label>
                                                                            <input
                                                                                type="number"
                                                                                step="0.01"
                                                                                value={commonData.requestPrice || ''}
                                                                                onChange={(e) => setPoCommonInventoryData(prev => ({
                                                                                    ...prev,
                                                                                    [product._id]: {
                                                                                        ...prev[product._id],
                                                                                        requestPrice: e.target.value
                                                                                    }
                                                                                }))}
                                                                                placeholder="0.00"
                                                                                className="w-full px-2 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                                                                            />
                                                                            <p className="text-[10px] text-gray-500 mt-1 mb-1">Price per 1 {poCommonInventoryData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel || 'unit'}</p>
                                                                        </div>

                                                                        {/* Margin (Auto-calculated) */}
                                                                        <div>
                                                                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                                                Margin (%) <span className="text-red-500">*</span>
                                                                            </label>
                                                                            <input
                                                                                type="number"
                                                                                step="0.01"
                                                                                value={(() => {
                                                                                    const originalPrice = parseFloat(commonData.originalPrice) || 0;
                                                                                    const requestPrice = parseFloat(commonData.requestPrice) || 0;
                                                                                    if (originalPrice <= 0) return '';
                                                                                    const margin = ((originalPrice - requestPrice) / originalPrice) * 100;
                                                                                    return Math.max(0, margin).toFixed(2);
                                                                                })()}
                                                                                disabled
                                                                                className="w-full px-2 py-2 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm cursor-not-allowed"
                                                                            />
                                                                        </div>

                                                                        {/* Required Quantity */}
                                                                        <div>
                                                                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                                                Required Quantity ({poCommonInventoryData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel || 'N/A'}) <span className="text-red-500">*</span>
                                                                            </label>
                                                                            <div className="flex items-center gap-2">
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    value={commonData.quantity || ''}
                                                                                    onChange={(e) => handlePoCommonInventoryQuantityChange(product._id, e.target.value)}
                                                                                    placeholder="0"
                                                                                    className="flex-1 px-2 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                                                                                />
                                                                                {(poCommonInventoryData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel) && (
                                                                                    <span className="text-xs font-semibold text-indigo-700 bg-indigo-100 px-2.5 py-2 rounded whitespace-nowrap border border-indigo-200">
                                                                                        {poCommonInventoryData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Show product options as disabled/grayscale when commonInventoryType is true - Bulk Style */}
                                                                {product.options && product.options.length > 0 && commonData?.isCommonInventory && (
                                                                    <div className="mt-3 space-y-2">
                                                                        {product.options.map((option) => {
                                                                            return (
                                                                                <div key={option._id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg opacity-40 pointer-events-none grayscale">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        disabled
                                                                                        className="w-5 h-5 text-purple-600 rounded focus:outline-none"
                                                                                    />
                                                                                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                                                                                        {typeof option.weight === 'object' && option.weight !== null
                                                                                            ? option.weight.weightLabel || `${option.weight.weightValue} ${option.weight.metricLabel}`
                                                                                            : option.weight}
                                                                                    </span>
                                                                                    <span className="text-xs text-gray-600">
                                                                                        ₹{option.originalPrice?.toFixed(2) || 0}
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            // Regular product option rendering
                                            return (
                                                <tr key={productId} className={isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'}>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected || false}
                                                            onChange={(e) => handleProductSelect(productId, e.target.checked)}
                                                            className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded cursor-pointer focus:outline-none"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                        {index + 1}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        {product.productName}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                        {typeof product.weight === 'object' && product.weight !== null
                                                            ? product.weight.weightLabel || `${product.weight.weightValue || ''} ${product.weight.metricLabel || ''}`.trim() || 'N/A'
                                                            : product.weight || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                        ₹{product.originalPrice.toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={data.requestPrice || ''}
                                                            onChange={(e) => handleRequestPriceChange(productId, e.target.value, product.originalPrice)}
                                                            disabled={!isSelected}
                                                            className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                            placeholder="0.00"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                            value={data.margin || ''}
                                                            onChange={(e) => handleMarginChange(productId, e.target.value, product.originalPrice)}
                                                            disabled={!isSelected}
                                                            className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={data.unitsRequired || ''}
                                                            onChange={(e) => handleUnitsChange(productId, e.target.value)}
                                                            disabled={!isSelected}
                                                            className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Summary */}
                    <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 rounded-lg shadow-lg border border-purple-100 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-700">Order Summary</h2>
                            {Object.keys(selectedProducts).filter(id => selectedProducts[id].selected).length > 0 && summary.totalProducts === 0 && (
                                <div className="text-sm text-orange-600 bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-200">
                                    ⚠️ Please fill Request Price (₹), Margin (%), and Units Required for all selected products
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div className="bg-white rounded-lg p-4 shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                                <p className="text-sm text-gray-600 mb-1 font-medium">Total Products</p>
                                <p className="text-2xl font-bold text-gray-800">{summary.totalProducts}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                                <p className="text-sm text-gray-600 mb-1 font-medium">Total Units</p>
                                <p className="text-2xl font-bold text-gray-800">{summary.totalUnits}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                                <p className="text-sm text-gray-600 mb-1 font-medium">Total MRP</p>
                                <p className="text-2xl font-bold text-gray-800">₹{summary.totalMRP}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-md border border-green-100 hover:shadow-lg transition-shadow">
                                <p className="text-sm text-gray-600 mb-1 font-medium">Total Request Price</p>
                                <p className="text-2xl font-bold text-green-600">₹{summary.totalRequestPrice}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-md border border-blue-100 hover:shadow-lg transition-shadow">
                                <p className="text-sm text-gray-600 mb-1 font-medium">Total Savings</p>
                                <p className="text-2xl font-bold text-blue-600">₹{summary.totalSavings}</p>
                            </div>
                        </div>
                    </div>

                    {/* Common Inventory Summary - Only show if there are common inventory items */}
                    {(() => {
                        const commonInvSummary = calculateCommonInventorySummary();
                        return commonInvSummary.hasCommonInventory && (
                            <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 rounded-lg shadow-lg border border-amber-100 p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-bold text-gray-700">Common Inventory - Metric-based Items</h2>
                                    <span className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-semibold">Summary</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-white rounded-lg p-4 shadow-md border border-amber-100 hover:shadow-lg transition-shadow">
                                        <p className="text-sm text-gray-600 mb-1 font-medium">Total MRP</p>
                                        <p className="text-2xl font-bold text-gray-800">₹{commonInvSummary.totalMRP}</p>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 shadow-md border border-green-100 hover:shadow-lg transition-shadow">
                                        <p className="text-sm text-gray-600 mb-1 font-medium">Total Request Price</p>
                                        <p className="text-2xl font-bold text-green-600">₹{commonInvSummary.totalRequestPrice}</p>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 shadow-md border border-blue-100 hover:shadow-lg transition-shadow">
                                        <p className="text-sm text-gray-600 mb-1 font-medium">Total Savings</p>
                                        <p className="text-2xl font-bold text-blue-600">₹{commonInvSummary.totalSavings}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </form>
            </div>
        </div>
    );
};

export default CreatePurchaseOrder;
